// React
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from '@refinedev/core';
import { useDebouncedCallback } from '@mantine/hooks';

// Mantine
import {
  Button,
  Flex,
  TextInput,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import {
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
  type MRT_TableInstance,
} from 'mantine-react-table';
import {
  IconFilterX,
  IconListDetails,
  IconSearch,
  IconSearchOff,
} from '@tabler/icons-react';

import type { QueryClient } from '@tanstack/react-query';

// Custom Components
import {
  InternalRefreshButton,
  InternalToggleClaimTotalsButton,
  InternalTogglePreviewButton,
  InternalExportTableButton,
} from 'components/common';
import type { Claim } from 'interfaces';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import { useClaimTabFilterStore } from 'store/ClaimTabFilterStore';
import { isMeaningfulFilterValue } from 'utils/filterUtils';
import { RestoreColumnsButton } from './RestoreColumnsButton';
import { ClaimsShowHideColumnsButton } from './ClaimsShowHideColumnsButton';

interface ClaimsToolbarInternalActionsProps {
  table?: MRT_TableInstance<Claim>;
  queryClient?: QueryClient;
  isProvider: boolean;
  placement?: 'external' | 'table';
}

const SYSTEM_MANAGED_FILTER_IDS = new Set([
  'actionRequest',
  'actionRequest.providerID',
  'actionRequest.userID',
  'completed',
  'inProgress',
  'tariffs.status',
]);

const hasActiveColumnFilter = (
  columnFilters?: MRT_ColumnFiltersState
): boolean =>
  (columnFilters ?? []).some(
    (filter) =>
      !SYSTEM_MANAGED_FILTER_IDS.has(filter.id) &&
      isMeaningfulFilterValue(filter.value)
  );

export const ClaimsToolbarInternalActions: React.FC<
  ClaimsToolbarInternalActionsProps
> = ({ table, queryClient, isProvider, placement = 'table' }) => {
  const { translate } = useTranslation();
  const toolbarIconSize = 24;
  const toolbarButtonSize = 32;
  const computedColorScheme = useComputedColorScheme('dark');
  const { resetPagination } = useClaimPaginationStore();
  const {
    tariffRowFilterMode,
    toggleTariffRowFilterMode,
    resetClaimTabFilters,
  } = useClaimTabFilterStore();
  const isTablePlacement = placement === 'table';
  const tableState = table?.getState();
  const showGlobalFilter = tableState?.showGlobalFilter ?? false;
  const globalFilterValue = (tableState?.globalFilter as string) ?? '';
  const [searchInput, setSearchInput] = useState(globalFilterValue);
  const applyGlobalFilter = useDebouncedCallback(
    (value: string) => table?.setGlobalFilter(value),
    500
  );
  useEffect(() => {
    applyGlobalFilter.cancel();
    setSearchInput(globalFilterValue);
  }, [applyGlobalFilter, globalFilterValue]);
  const hasColumnFilters = hasActiveColumnFilter(tableState?.columnFilters);
  const showColumnFilters = tableState?.showColumnFilters ?? true;
  const hasGlobalFilterValue = Boolean(globalFilterValue);
  const isShowAllTariffs = tariffRowFilterMode === 'showAllTariffs';
  const tariffRowToggleLabel = isShowAllTariffs
    ? translate(
        'pages:claims.tariffRows.showMatchingOnly',
        'Show matching tariff rows only'
      )
    : translate('pages:claims.tariffRows.showAll', 'Show all tariffs');
  const isClearFiltersActive = hasColumnFilters || hasGlobalFilterValue;
  const iconColor = computedColorScheme === 'dark' ? 'white' : 'black';

  const handleClearFilters = () => {
    if (!table) return;
    applyGlobalFilter.cancel();
    setSearchInput('');
    resetClaimTabFilters(isProvider);
    resetPagination();
    table.resetSorting(true);
    table.resetColumnFilters(true);
    table.resetGlobalFilter(true);
    table.setShowColumnFilters(true);
    table.setShowGlobalFilter(false);
  };

  const handleToggleGlobalFilter = () => {
    if (!table) return;
    if (showGlobalFilter) {
      applyGlobalFilter.cancel();
      setSearchInput('');
      table.setGlobalFilter('');
      table.setShowGlobalFilter(false);
      return;
    }
    table.setShowGlobalFilter(true);
  };

  return (
    <Flex align="center" gap="0.313rem">
      {isTablePlacement ? (
        <>
          {showGlobalFilter ? (
            <TextInput
              value={searchInput}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setSearchInput(value);
                applyGlobalFilter(value);
              }}
              placeholder="Search Claims"
              w={220}
              size="sm"
              leftSection={<IconSearch size={16} />}
              aria-label="Search Claims"
              data-testid="claims-inline-global-search"
            />
          ) : null}
          {queryClient ? (
            <InternalRefreshButton queryClient={queryClient} />
          ) : null}
          <InternalExportTableButton />
          <Tooltip
            label={showGlobalFilter ? 'Hide search' : 'Show search'}
            withArrow
          >
            <Button
              onClick={handleToggleGlobalFilter}
              aria-label={showGlobalFilter ? 'Hide search' : 'Show search'}
              className="global-filter-button"
              variant="subtle"
              size="xs"
              p={0}
              style={{
                width: toolbarButtonSize,
                height: toolbarButtonSize,
                minWidth: toolbarButtonSize,
              }}
              data-testid="claims-toggle-global-search"
            >
              {showGlobalFilter ? (
                <IconSearchOff size={toolbarIconSize} color={iconColor} />
              ) : (
                <IconSearch size={toolbarIconSize} color={iconColor} />
              )}
            </Button>
          </Tooltip>
          <Tooltip label={tariffRowToggleLabel} withArrow>
            <Button
              onClick={toggleTariffRowFilterMode}
              aria-label={tariffRowToggleLabel}
              data-testid="toggle-matching-tariff-rows-button"
              variant="subtle"
              size="xs"
              p={0}
              style={{
                width: toolbarButtonSize,
                height: toolbarButtonSize,
                minWidth: toolbarButtonSize,
                ...(isShowAllTariffs
                  ? {
                      backgroundColor: 'var(--mantine-color-blue-7)',
                      border: '1px solid var(--mantine-color-blue-5)',
                    }
                  : {}),
              }}
            >
              <IconListDetails
                size={toolbarIconSize}
                color={isShowAllTariffs ? 'white' : iconColor}
              />
            </Button>
          </Tooltip>
          <Tooltip label="Clear filters" withArrow>
            <Button
              onClick={handleClearFilters}
              aria-label="Clear filters"
              data-testid="clear-claim-filters-button"
              variant="subtle"
              color={isClearFiltersActive ? 'blue' : 'gray'}
              size="xs"
              p={0}
              style={{
                width: toolbarButtonSize,
                height: toolbarButtonSize,
                minWidth: toolbarButtonSize,
                ...(isClearFiltersActive
                  ? {
                      backgroundColor: 'var(--mantine-color-blue-7)',
                      border: '1px solid var(--mantine-color-blue-5)',
                    }
                  : {}),
              }}
            >
              <IconFilterX
                size={toolbarIconSize}
                color={isClearFiltersActive ? 'white' : iconColor}
              />
            </Button>
          </Tooltip>
          {!isProvider && table ? (
            <MRT_ToggleFiltersButton
              table={table}
              title={
                showColumnFilters
                  ? 'Hide column filters'
                  : 'Show column filters'
              }
              aria-label={
                showColumnFilters
                  ? 'Hide column filters'
                  : 'Show column filters'
              }
              size="sm"
              variant="subtle"
              style={{
                width: toolbarButtonSize,
                height: toolbarButtonSize,
                minWidth: toolbarButtonSize,
                color: iconColor,
              }}
              className="toggle-filter-button"
              data-testid="claims-toggle-column-filters"
            />
          ) : null}
          {table ? (
            <ClaimsShowHideColumnsButton
              table={table}
              className="show-hide-columns-button"
            />
          ) : null}
          {table ? (
            <RestoreColumnsButton table={table} isProvider={isProvider} />
          ) : null}
        </>
      ) : (
        <>
          <InternalToggleClaimTotalsButton />
          <InternalTogglePreviewButton />
        </>
      )}
    </Flex>
  );
};
